import type { ReactNode } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

/**
 * Ask before doing something that cannot be quietly undone.
 *
 * Approving, declining, completing, disabling — each of these is somebody's signature on a
 * decision other people will act on, and each was one stray click away. A misfired approval
 * is not a UI annoyance: it moves work into Completed and tells everyone watching that a
 * person signed off on something they never read.
 *
 * The question names the action in the same words as the button that opened it, so the answer
 * is about the thing the person meant to do rather than about a generic "are you sure". No
 * closes and changes nothing — the item is exactly where it was.
 *
 * Deliberately NOT applied to reversible edits. A confirmation on everything is a confirmation
 * on nothing: people learn the rhythm of dismissing it and stop reading, which costs the real
 * warnings their meaning.
 */
export function ConfirmAction({
  action,
  subject,
  question,
  description,
  tone = 'default',
  disabled,
  onConfirm,
  children,
}: {
  /** The verb, as the user sees it on the button: "Approve", "Decline", "Disable". */
  action: string
  /** What it happens to: "this work item", "Acme Corp". */
  subject: string
  /**
   * The whole question, when verb-then-subject cannot say it in English. "Mark as a former
   * client" plus a name reads as "mark as a former client Acme Corp"; some actions simply
   * need their own sentence.
   */
  question?: string
  /** What follows from it, when that is not obvious. One sentence. */
  description?: ReactNode
  tone?: 'default' | 'danger'
  disabled?: boolean
  onConfirm: () => void
  /** The button that would have done the thing directly. */
  children: ReactNode
}) {
  if (disabled) return <>{children}</>

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {question ?? `Are you sure you want to ${action.toLowerCase()} ${subject}?`}
          </AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>No</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={cn(
              tone === 'danger' && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            )}
          >
            Yes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
